import { Fragment, useState, type ReactNode } from "react";
import { FileText, ChevronDown, ChevronRight } from "lucide-react";
import { FORM_CONTENT, formTypeTitle, type FormClause } from "@/lib/consent-forms";

/**
 * Renders a single sample consent/agreement document from the REAL FORM_CONTENT
 * data, structurally (React escapes everything — no raw-HTML injection). Light
 * "paper" card with a non-removable SAMPLE watermark (Scope DO-NOT + review item).
 * Shared by the add-client consent step and the sample-agreements view.
 */
export function SampleConsentDoc({ formType, states, clinicName, defaultOpen }: { formType: string; states: string[]; clinicName: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const def = FORM_CONTENT[formType];

  const clauses: FormClause[] = [...(def?.clauses ?? [])];
  if (def?.stateAddenda) {
    for (const s of Array.from(new Set(states))) {
      const addenda = def.stateAddenda[s];
      if (addenda) clauses.push(...addenda);
    }
  }
  const preamble = (def?.preamble ?? "").replace("{CLINIC_NAME}", clinicName);

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)]">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 font-heading text-[15px] font-medium text-[var(--color-text-primary)]">
          <FileText className="h-4 w-4 text-[var(--color-gold-soft)]" /> {formTypeTitle(formType)}
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-[var(--color-text-ghost)]" /> : <ChevronRight className="h-4 w-4 text-[var(--color-text-ghost)]" />}
      </button>
      {open && (
        <div className="relative border-t border-[var(--color-border)] bg-[#f7f3ec] p-5">
          <div className="pointer-events-none mb-3 inline-block rounded border border-[#d8a23a] bg-[#fff6e0] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[#7a5000]">
            Sample — not a legal document · no signature captured
          </div>
          <div className="consent-doc max-h-[320px] overflow-y-auto pr-2">
            {def ? (
              <>
                {preamble && <p className="preamble">{renderText(preamble)}</p>}
                {clauses.map((c, i) => (
                  <div key={i} className="clause">
                    <h3>{c.heading}</h3>
                    <p>{renderText(c.body)}</p>
                  </div>
                ))}
                <p className="required-by">Sample · {Array.from(new Set(states)).join(", ")}</p>
              </>
            ) : (
              <p>Sample content for &ldquo;{formType}&rdquo; is not available in this demo.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Render template body text: \n\n → paragraph breaks, \n → line breaks, **x** → bold. Text is escaped by React. */
function renderText(text: string): ReactNode {
  return text.split(/\n\n/).map((para, pi) => (
    <Fragment key={pi}>
      {pi > 0 && <br />}
      {pi > 0 && <br />}
      {renderInline(para)}
    </Fragment>
  ));
}

function renderInline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return (
      <Fragment key={i}>
        {part.split("\n").map((line, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </Fragment>
    );
  });
}
